import { CommonModule } from '@angular/common';
import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { RouterModule } from '@angular/router';
import { PermissionForStudents } from '@edtech-ui/access-role-permissions/users-permissions-constants/student-roles-permissions.enum';
import { HasPermissionDirective } from '@edtech-ui/access-role-permissions/users-permissions-directives/has-permissions.directives';
import { UserDetailResponse } from '@edtech-ui/core/models/Classroom/user-detail';
import { PaginationActive, TransactionItem } from '@edtech-ui/core/models/student-courses-v2/student-transcation-payments';
import { AuthServiceService } from '@edtech-ui/core/services/authService/auth-service.service';
import { DateService } from '@edtech-ui/core/services/dateService/date.service';
import { PaymentService } from '@edtech-ui/core/services/payment-management/payment.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
	selector: 'app-payment-history-home',
	standalone: true,
	imports: [CommonModule, MatTooltipModule, MatProgressSpinnerModule, RouterModule,
		HasPermissionDirective
	],
	templateUrl: './payment-history-home.component.html',
	styleUrl: './payment-history-home.component.scss'
})
export class PaymentHistoryHomeComponent implements OnInit, OnDestroy {

	@Output() close = new EventEmitter<void>();
	userDetail: UserDetailResponse['Data'] | null = null;
	subscription = new Subscription();
	userId = '';
	isLoading = false;
	studentTranscation: TransactionItem[] = [];
	pagination!: PaginationActive;
	currentPlanName = '';
	totalInvoices = 0;
	subscriptionJoinDate = '';
	dateFormat = DateService.APP_DATE_TIME_SHORT;
	page = 1;
	limit = 10;
	totalRecords = 0;
	totalPages = 0;
	visiblePages: number[] = [];
	PermissionForStudents = PermissionForStudents;

	constructor(
		private _authService: AuthServiceService,
		private _toastr: ToastrService,
		private _paymentService: PaymentService,
	) { }


	ngOnInit(): void {
		const storedId = localStorage.getItem('user_id');
		if (storedId) {
			this.userId = storedId;
			this.getUserInfo(this.userId);
		}
		this.getStudentTranscationDetails();
	}

	getUserInfo(id: string) {
		this.isLoading = true;
		this.subscription.add(
			this._authService.getUserDetails(id).pipe(
				finalize(() => {
					this.isLoading = false;
				})
			).subscribe({
				next: (response: UserDetailResponse) => {
					if (response.Success) {
						this.userDetail = response.Data;
					}
				},
				error: () => this._toastr.error('Something went wrong while fecthing the user Details')
			})
		);
	}

	getStudentTranscationDetails() {
		this.isLoading = true;
		this._paymentService.getStudentsTranscations(this.page, this.limit).pipe(
			tap(response => {
				if (response.Success) {
					this.studentTranscation = response.Data.transactions;

					this.currentPlanName = response.Data.current_plan_name;
					this.totalInvoices = response.Data.total_invoices;
					this.subscriptionJoinDate = response.Data.user_join_date;

					this.page = response.Data.pagination.current_page;
					this.limit = response.Data.pagination.page_size;
					this.totalRecords = response.Data.pagination.total_items;
					this.totalPages = response.Data.pagination.total_pages;
					this.updateVisiblePages();
				}
				else {
					this._toastr.error(response.Message);
				}
			}),
			finalize(() => {
				this.isLoading = false;
			}),
			catchError(() => {
				this._toastr.error('Something went wrong.');
				return of(null);
			})
		).subscribe()
	}

	getPaymentTranscationDownload(orderId: string) {
		this.isLoading = true;

		this._paymentService.getPaymentTranscationDownload(orderId).pipe(
			finalize(() => this.isLoading = false),
			catchError(() => {
				this._toastr.error('Unable to download invoice');
				return of(null);
			})
		).subscribe(blob => {
			if (!blob) return;

			const fileURL = window.URL.createObjectURL(blob);
			window.open(fileURL, '_blank');

			setTimeout(() => {
				const anchor = document.createElement('a');
				anchor.href = fileURL;
				anchor.download = `invoice-${orderId}.pdf`;
				anchor.click();

				window.URL.revokeObjectURL(fileURL);
			}, 300);
		});
	}

	changePage(page: number): void {
		if (page < 1 || page > this.totalPages || page === this.page) return;
		this.page = page;
		this.getStudentTranscationDetails();
	}

	updateVisiblePages(): void {
		const pages: number[] = [];
		const start = Math.max(1, this.page - 2);
		const end = Math.min(this.totalPages, this.page + 2);

		for (let i = start; i <= end; i++) {
			pages.push(i);
		}

		this.visiblePages = pages;
	}


	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}
}